/**
 * ComponentPalette - Draggable component list for the worksheet builder
 *
 * Features:
 * - Grouped by category (Text, Numbers, Multiple Choice, Drawing, Math)
 * - Draggable items using @dnd-kit
 * - Visual icons and descriptions
 * - Collapsible categories
 */

import { useMemo, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Type,
  FileText,
  Hash,
  Binary,
  Circle,
  Pencil,
  Calculator,
  BarChart2,
  Grid3X3,
  ChevronDown,
  GripVertical,
} from "lucide-react";
import {
  CATEGORIES,
  COMPONENT_DEFINITIONS,
  getComponentsByCategory,
  type ComponentDefinition,
  type ComponentCategory,
} from "./types";

// ============================================================================
// Icon Mapper
// ============================================================================

const iconMap: Record<string, React.ElementType> = {
  User,
  Type,
  FileText,
  Hash,
  Binary,
  Circle,
  Pencil,
  Calculator,
  BarChart2,
  Grid3X3,
};

function getIcon(iconName: string): React.ElementType {
  return iconMap[iconName] || FileText;
}

// ============================================================================
// Draggable Palette Item
// ============================================================================

interface PaletteItemProps {
  definition: ComponentDefinition;
}

function PaletteItem({ definition }: PaletteItemProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `palette-${definition.type}`,
      data: {
        type: "palette-item",
        definition,
      },
    });

  const Icon = getIcon(definition.icon);

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 1000 : undefined,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg border bg-card cursor-grab",
        "hover:border-primary/50 hover:bg-accent/50 transition-colors",
        isDragging && "opacity-50 shadow-lg border-primary"
      )}
      {...listeners}
      {...attributes}
    >
      <div className="flex items-center gap-2 shrink-0">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
        <div
          className={cn(
            "p-2 rounded-md",
            definition.category === "text" && "bg-purple-100 text-purple-700",
            definition.category === "numbers" && "bg-blue-100 text-blue-700",
            definition.category === "multiple_choice" &&
              "bg-green-100 text-green-700",
            definition.category === "drawing" &&
              "bg-orange-100 text-orange-700",
            definition.category === "math" && "bg-red-100 text-red-700"
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-none">{definition.label}</p>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
          {definition.description}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Category Section
// ============================================================================

interface CategorySectionProps {
  category: ComponentCategory;
  label: string;
  icon: string;
  color: string;
  defaultOpen?: boolean;
}

function CategorySection({
  category,
  label,
  icon,
  color,
  defaultOpen = true,
}: CategorySectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const components = useMemo(
    () => getComponentsByCategory(category),
    [category]
  );
  const Icon = getIcon(icon);

  if (components.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-md hover:bg-accent transition-colors">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-4 w-4", color)} />
          <span className="text-sm font-medium">{label}</span>
          <Badge variant="secondary" className="text-xs">
            {components.length}
          </Badge>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 space-y-2">
        {components.map((def) => (
          <PaletteItem key={def.type} definition={def} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export interface ComponentPaletteProps {
  className?: string;
}

export function ComponentPalette({ className }: ComponentPaletteProps) {
  return (
    <div
      className={cn("flex flex-col h-full border-r bg-muted/30", className)}
    >
      <div className="p-4 border-b">
        <h2 className="font-semibold text-sm">Components</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Drag components to the canvas
        </p>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {CATEGORIES.map((cat, index) => (
            <CategorySection
              key={cat.id}
              category={cat.id}
              label={cat.label}
              icon={cat.icon}
              color={cat.color}
              defaultOpen={index < 2} // Keep first two categories open by default
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
